// client/input — secret: WASD + click-to-move pipeline, ability hotkey routing.
// Public surface: subscribe(handler), bind(action, key).

export type Intent =
  | { kind: 'moveAxis'; x: number; z: number }
  | { kind: 'moveTo'; clientX: number; clientY: number }
  | { kind: 'togglePanel'; panel: 'inventory' }
  | { kind: 'craftToggle' };

type Handler = (i: Intent) => void;
const handlers = new Set<Handler>();
let installed = false;

const keyState = new Set<string>();
let lastAxis = { x: 0, z: 0 };

function emit(i: Intent): void {
  for (const h of handlers) h(i);
}

function recomputeAxis(): void {
  let x = 0;
  let z = 0;
  if (keyState.has('w') || keyState.has('arrowup')) z -= 1;
  if (keyState.has('s') || keyState.has('arrowdown')) z += 1;
  if (keyState.has('a') || keyState.has('arrowleft')) x -= 1;
  if (keyState.has('d') || keyState.has('arrowright')) x += 1;
  if (x !== 0 || z !== 0) {
    const len = Math.hypot(x, z);
    x /= len;
    z /= len;
  }
  if (x !== lastAxis.x || z !== lastAxis.z) {
    lastAxis = { x, z };
    emit({ kind: 'moveAxis', x, z });
  }
}

function onKeyDown(e: KeyboardEvent): void {
  const k = e.key.toLowerCase();
  if (keyState.has(k)) return;
  keyState.add(k);

  if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
    recomputeAxis();
    return;
  }
  const keyMap: Record<string, Intent | undefined> = {
    i: { kind: 'togglePanel', panel: 'inventory' },
    c: { kind: 'craftToggle' },
  };
  const intent = keyMap[k];
  if (intent) emit(intent);
}

function onKeyUp(e: KeyboardEvent): void {
  const k = e.key.toLowerCase();
  keyState.delete(k);
  if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
    recomputeAxis();
  }
}

function onClick(e: MouseEvent): void {
  if (e.button !== 0) return;
  const target = e.target as HTMLElement | null;
  if (target && target.closest && target.closest('#ui-root .panel, #ui-root .hud')) {
    return;
  }
  emit({ kind: 'moveTo', clientX: e.clientX, clientY: e.clientY });
}

function onContextMenu(e: MouseEvent): void {
  // Suppress right-click menu on canvas to avoid interrupting gameplay.
  const target = e.target as HTMLElement | null;
  if (target && target.tagName === 'CANVAS') {
    e.preventDefault();
  }
}

export function install(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('mousedown', onClick);
  window.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('blur', () => {
    keyState.clear();
    if (lastAxis.x !== 0 || lastAxis.z !== 0) {
      lastAxis = { x: 0, z: 0 };
      emit({ kind: 'moveAxis', x: 0, z: 0 });
    }
  });
}

export function subscribe(h: Handler): () => void {
  handlers.add(h);
  return () => handlers.delete(h);
}

export function currentAxis(): Readonly<{ x: number; z: number }> {
  return lastAxis;
}
